import { useState, useMemo } from 'react'
import { DocumentFilters } from './DocumentFilters'
import { MonthlyGroup } from './MonthlyGroup'
import { Pagination } from '@/components/common'
import { useDocuments } from '@/hooks/useDocuments'
import { getPresignedUrl } from '@/services/documentsApi'
import { groupDocumentsByMonth } from '@/utils/grouping'
import type { DocumentFilters as Filters } from '@/types'

interface DocumentListProps {
  businessId: string
  onDocumentSelect: (id: string) => void
}

const PAGE_SIZE = 50

export function DocumentList({ businessId, onDocumentSelect }: DocumentListProps) {
  const [filters, setFilters] = useState<Filters>({})
  const [page, setPage] = useState(1)

  const { data, isLoading, error, refetch } = useDocuments({
    business_id: businessId,
    document_type: filters.documentType,
    document_status: filters.documentStatus,
    sumit_status: filters.sumitStatus,
    date_from: filters.dateFrom,
    date_to: filters.dateTo,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  })

  const monthlyGroups = useMemo(() => {
    if (!data?.documents) return []
    return groupDocumentsByMonth(data.documents)
  }, [data?.documents])

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 0

  const handleFiltersChange = (newFilters: Filters) => {
    setFilters(newFilters)
    setPage(1) // Reset to first page when filters change
  }

  const handleDownload = async (s3Key: string) => {
    try {
      const url = await getPresignedUrl(s3Key)
      window.open(url, '_blank')
    } catch (err) {
      console.error('Failed to get download URL:', err)
    }
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg" data-testid="error-state">
        <p className="text-red-800">Failed to load documents</p>
        <button
          onClick={() => refetch()}
          className="mt-2 text-red-600 hover:text-red-800 underline"
        >
          Try again
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-4" data-testid="document-list">
      <DocumentFilters filters={filters} onFiltersChange={handleFiltersChange} />

      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600" data-testid="document-count">
          {isLoading ? (
            <span>Loading...</span>
          ) : (
            <span>{data?.total ?? 0} document{data?.total !== 1 ? 's' : ''}</span>
          )}
        </div>
        {totalPages > 1 && (
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8" data-testid="loading-state">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : monthlyGroups.length === 0 ? (
        <div className="text-center py-8 text-gray-500" data-testid="empty-state">
          No documents found
        </div>
      ) : (
        <>
          {monthlyGroups.map((group) => (
            <MonthlyGroup
              key={group.month}
              group={group}
              onDocumentSelect={onDocumentSelect}
              onDocumentDownload={handleDownload}
            />
          ))}
        </>
      )}
    </div>
  )
}
